import { Rng, fnv1a } from './Rng';

/**
 * Named seeded streams, derived per (runSeed, name, floor).
 *
 * The determinism contract (design doc §3):
 * - Seeded: map layout, spawn/elite schedule, loot roll order, shop inventory,
 *   floor-event placement/contents, vault layout + vault loot.
 * - NOT seeded: moment-to-moment enemy behavior, exact spawn tiles, cosmetics.
 *
 * Per-floor derivation means floor 2's map never depends on how many draws
 * floor 1 consumed. Systems receive only the specific Rng they may use.
 *
 * `events` fixes event count/types and all event contents (merchant stock,
 * chest artifact, ambush elites, shrine offer, vault-door presence).
 * `vaultMap`/`vaultLoot`/`vaultContent` are keyed by the ORIGIN floor and
 * drive the floor's vault side-area layout, its Keeper drop, and its
 * altar-vs-chest contents roll — separate names so a vault visit can never
 * re-consume the origin floor's streams.
 * `goblin` fixes the treasure goblin: its existence roll, guaranteed drop
 * (id + quality + proc), and the second-drop chance.
 */
export type StreamName =
  | 'map' | 'spawns' | 'loot' | 'shop' | 'levelups'
  | 'events' | 'vaultMap' | 'vaultLoot' | 'vaultContent' | 'boss'
  | 'scatter' | 'hazards' | 'goblin';

export class RngStreams {
  constructor(readonly runSeed: string) {}

  stream(name: StreamName, floor: number): Rng {
    return new Rng(fnv1a(`${this.runSeed}:${name}:${floor}`));
  }

  /**
   * Per-emitter pattern stream, keyed by the deterministic spawn ordinal so one
   * emitter's rolls (gap placement, jitter) never perturb another's.
   */
  patternStream(floor: number, spawnOrdinal: number): Rng {
    return new Rng(fnv1a(`${this.runSeed}:pattern:${floor}:${spawnOrdinal}`));
  }
}

/**
 * Unseeded RNG for everything reactive/cosmetic: AI jitter, exact spawn tiles,
 * particles, shake noise. A physically separate instance so grabbing a seeded
 * stream for cosmetics (or vice versa) is grep-able.
 */
export const gameplayRng = new Rng((Math.random() * 2 ** 32) >>> 0);
